import { useEffect, useMemo, useState } from 'react'
import {
  activatePolicyVersion,
  createPolicyVersion,
  dryRunPolicy,
  getPolicyVersion,
  listPolicies,
  listPolicyAudit,
  seedPolicies,
  type PolicyAuditItem,
  type PolicySummary,
  type PolicyVersionDetail,
} from '../../api/moderation'
import { mapActionKeyToLabel } from '../../lib/moderationLabels'

const CONTENT_TYPES = [
  { value: 'post', label: 'Post' },
  { value: 'comment', label: 'Comment' },
  { value: 'profile', label: 'Profile' },
  { value: 'listing', label: 'Listing' },
  { value: 'handle', label: 'Handle' },
]

const POLICY_TEMPLATES: Record<string, Record<string, any>> = {
  default: {
    thresholds: {
      escalate_conf: 0.55,
      block_conf: 0.7,
      remove_conf: 0.85,
    },
    trust_adjustments: {
      low: {
        severity_delta: 10,
        escalate_conf: 0.55,
        soft_block_conf: 0.7,
      },
      high: {
        remove_to_escalate_conf: 0.9,
        allow_review_conf: 0.7,
      },
    },
    rule_toggles: {
      spam_keywords_v1: true,
      scam_keywords_v1: true,
      shortener_domain_v1: true,
    },
    weights: {
      spam_keywords_v1: 1,
      scam_keywords_v1: 1,
    },
  },
  profile: {
    thresholds: {
      escalate_conf: 0.55,
      block_conf: 0.7,
      remove_conf: 0.85,
    },
    rule_toggles: {
      reserved_handle_v1: true,
      forbidden_handle_terms_v1: true,
      misleading_handle_terms_v1: true,
    },
    reserved_handles: {
      add: ['support', 'help'],
      remove: [],
    },
  },
  listing: {
    thresholds: {
      escalate_conf: 0.55,
      block_conf: 0.7,
      remove_conf: 0.85,
    },
    rule_toggles: {
      listing_payment_steering_v1: true,
      listing_counterfeit_cues_v1: true,
      listing_suspicious_price_v1: true,
    },
  },
}

const DRY_RUN_SAMPLES: Record<string, { label: string; apply: () => {
  type: string
  text: string
  links: string
  context: Record<string, any>
} }> = {
  post_scam: {
    label: 'Post: scam keywords + short link',
    apply: () => ({
      type: 'post',
      text: 'Verify your account now. Click link for free money.',
      links: 'https://t.co/offer',
      context: {},
    }),
  },
  profile_reserved: {
    label: 'Profile: reserved handle',
    apply: () => ({
      type: 'profile',
      text: 'Official support account',
      links: '',
      context: { handle: 'PixeloopSupport', display_name: 'Pixeloop Support' },
    }),
  },
  listing_payment: {
    label: 'Listing: payment steering + low price',
    apply: () => ({
      type: 'listing',
      text: 'Pay via CashApp for fast delivery.',
      links: 'https://bit.ly/pay',
      context: {
        title: 'Luxury bag',
        subtitle: 'Limited offer',
        description: 'Pay with CashApp only.',
        price: 5,
        currency: 'usd',
      },
    }),
  },
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  )
}

export default function ModerationPoliciesPage() {
  const [policies, setPolicies] = useState<PolicySummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPolicyKey, setSelectedPolicyKey] = useState<string | null>(null)
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  const [versionDetail, setVersionDetail] = useState<PolicyVersionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [newText, setNewText] = useState('')
  const [newConfig, setNewConfig] = useState('{}')
  const [newError, setNewError] = useState<string | null>(null)
  const [newJsonError, setNewJsonError] = useState<string | null>(null)
  const [newBackendErrors, setNewBackendErrors] = useState<string[]>([])
  const [newSaving, setNewSaving] = useState(false)

  const [dryType, setDryType] = useState('post')
  const [dryActor, setDryActor] = useState('')
  const [dryText, setDryText] = useState('')
  const [dryLinks, setDryLinks] = useState('')
  const [dryContext, setDryContext] = useState('{}')
  const [dryResponse, setDryResponse] = useState<any | null>(null)
  const [dryError, setDryError] = useState<string | null>(null)
  const [dryLoading, setDryLoading] = useState(false)
  const [drySample, setDrySample] = useState('')

  const [auditItems, setAuditItems] = useState<PolicyAuditItem[]>([])
  const [auditError, setAuditError] = useState<string | null>(null)
  const [auditLoading, setAuditLoading] = useState(false)

  const selectedPolicy = useMemo(
    () => policies.find((policy) => policy.key === selectedPolicyKey) || null,
    [policies, selectedPolicyKey]
  )

  useEffect(() => {
    setLoading(true)
    setError(null)
    listPolicies()
      .then((data) => {
        setPolicies(data)
        if (!selectedPolicyKey && data.length > 0) {
          setSelectedPolicyKey(data[0].key)
        }
      })
      .catch((err: any) => setError(err?.response?.data?.detail || err?.message || 'Failed to load policies.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    setAuditLoading(true)
    setAuditError(null)
    listPolicyAudit(10)
      .then((data) => setAuditItems(data.items || []))
      .catch((err: any) => setAuditError(err?.response?.data?.detail || err?.message || 'Failed to load audit.'))
      .finally(() => setAuditLoading(false))
  }, [])

  useEffect(() => {
    if (!selectedPolicy) {
      setSelectedVersionId(null)
      setVersionDetail(null)
      return
    }
    const active = selectedPolicy.versions.find((version) => version.is_active)
    const nextId = active?.id || selectedPolicy.versions[0]?.id || null
    setSelectedVersionId(nextId)
  }, [selectedPolicy])

  useEffect(() => {
    if (!selectedPolicyKey || !selectedVersionId) {
      setVersionDetail(null)
      return
    }
    setDetailLoading(true)
    setDetailError(null)
    getPolicyVersion(selectedPolicyKey, selectedVersionId)
      .then((data) => setVersionDetail(data))
      .catch((err: any) => setDetailError(err?.response?.data?.detail || err?.message || 'Failed to load version.'))
      .finally(() => setDetailLoading(false))
  }, [selectedPolicyKey, selectedVersionId])

  const refreshPolicies = async () => {
    const data = await listPolicies()
    setPolicies(data)
    if (!selectedPolicyKey && data.length > 0) {
      setSelectedPolicyKey(data[0].key)
    }
  }

  const refreshAudit = async () => {
    try {
      const data = await listPolicyAudit(10)
      setAuditItems(data.items || [])
    } catch (e: any) {
      setAuditError(e?.response?.data?.detail || e?.message || 'Failed to load audit.')
    }
  }

  const handleSeedDefaults = async () => {
    setError(null)
    try {
      await seedPolicies()
      await refreshPolicies()
      await refreshAudit()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to seed default policies.')
    }
  }

  const formatJson = () => {
    setNewError(null)
    setNewBackendErrors([])
    try {
      const parsed = newConfig.trim() ? JSON.parse(newConfig) : {}
      setNewConfig(JSON.stringify(parsed, null, 2))
      setNewJsonError(null)
    } catch (e: any) {
      setNewJsonError('Config must be valid JSON.')
    }
  }

  const resetToTemplate = () => {
    if (!selectedPolicyKey) return
    const template = POLICY_TEMPLATES[selectedPolicyKey] || {}
    setNewConfig(JSON.stringify(template, null, 2))
    setNewJsonError(null)
    setNewBackendErrors([])
  }

  const openCreateModal = () => {
    setShowCreate(true)
    setNewText('')
    setNewError(null)
    setNewJsonError(null)
    setNewBackendErrors([])
    if (selectedPolicyKey) {
      const template = POLICY_TEMPLATES[selectedPolicyKey] || {}
      setNewConfig(JSON.stringify(template, null, 2))
    } else {
      setNewConfig('{}')
    }
  }

  const handleCreate = async () => {
    if (!selectedPolicyKey) return
    setNewError(null)
    setNewBackendErrors([])
    let parsedConfig: any = {}
    try {
      parsedConfig = newConfig.trim() ? JSON.parse(newConfig) : {}
      setNewJsonError(null)
    } catch (e: any) {
      setNewJsonError('Config must be valid JSON.')
      return
    }
    setNewSaving(true)
    try {
      const resp = await createPolicyVersion(selectedPolicyKey, { text: newText, config: parsedConfig })
      await refreshPolicies()
      setSelectedVersionId(resp.id)
      setShowCreate(false)
      setNewText('')
      setNewConfig('{}')
      await refreshAudit()
    } catch (e: any) {
      const errors = e?.response?.data?.errors
      if (Array.isArray(errors)) {
        setNewBackendErrors(errors)
      } else {
        setNewError(e?.response?.data?.detail || e?.message || 'Failed to create version.')
      }
    } finally {
      setNewSaving(false)
    }
  }

  const handleActivate = async (versionId: string) => {
    if (!selectedPolicyKey) return
    const version = selectedPolicy?.versions.find((item) => item.id === versionId)
    const confirmed = window.confirm(
      `Activate version ${version?.version ?? ''} for ${selectedPolicyKey}?`
    )
    if (!confirmed) return
    try {
      setDetailError(null)
      await activatePolicyVersion(selectedPolicyKey, versionId)
      await refreshPolicies()
      setSelectedVersionId(versionId)
      await refreshAudit()
    } catch (e: any) {
      setDetailError(e?.response?.data?.detail || e?.message || 'Failed to activate version.')
    }
  }

  const handleDryRun = async () => {
    setDryError(null)
    setDryResponse(null)
    let parsedContext: any = {}
    try {
      parsedContext = dryContext.trim() ? JSON.parse(dryContext) : {}
    } catch (e: any) {
      setDryError('Context must be valid JSON.')
      return
    }
    const linkItems = dryLinks
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ url }))
    setDryLoading(true)
    try {
      const resp = await dryRunPolicy({
        content_type: dryType as any,
        actor_user_id: dryActor || undefined,
        text: dryText,
        context: parsedContext,
        links: linkItems,
      })
      setDryResponse(resp)
      await refreshAudit()
    } catch (e: any) {
      setDryError(e?.response?.data?.detail || e?.message || 'Dry run failed.')
    } finally {
      setDryLoading(false)
    }
  }

  const handleApplySample = (value: string) => {
    setDrySample(value)
    if (!value) return
    const sample = DRY_RUN_SAMPLES[value]
    if (!sample) return
    const payload = sample.apply()
    setDryType(payload.type)
    setDryText(payload.text)
    setDryLinks(payload.links)
    setDryContext(JSON.stringify(payload.context || {}, null, 2))
    setDryError(null)
    setDryResponse(null)
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="lg:col-span-4 space-y-4">
        <Panel title="Policies">
          {loading && <div className="text-sm text-white/70">Loading policies...</div>}
          {error && <div className="text-sm text-red-200">{error}</div>}
          {!loading && policies.length === 0 && (
            <div className="space-y-3 text-sm text-white/70">
              <div>No policies configured yet.</div>
              <button
                onClick={handleSeedDefaults}
                className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5"
              >
                Create default policies
              </button>
            </div>
          )}
          <div className="space-y-2">
            {policies.map((policy) => (
              <button
                key={policy.key}
                onClick={() => setSelectedPolicyKey(policy.key)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${
                  policy.key === selectedPolicyKey
                    ? 'border-white/20 bg-white/10'
                    : 'border-white/10 hover:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{policy.name || policy.key}</span>
                  {policy.active_version ? (
                    <span className="text-xs text-white/60">v{policy.active_version}</span>
                  ) : (
                    <span className="text-xs text-white/40">No active</span>
                  )}
                </div>
                {policy.description && <div className="text-xs text-white/60">{policy.description}</div>}
              </button>
            ))}
          </div>
        </Panel>
        <Panel title="Dry-run tester">
          <div className="space-y-3 text-sm">
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Content type</div>
              <select
                className="w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm"
                value={dryType}
                onChange={(e) => setDryType(e.target.value)}
              >
                {CONTENT_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Sample inputs</div>
              <select
                className="w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm"
                value={drySample}
                onChange={(e) => handleApplySample(e.target.value)}
              >
                <option value="">Select a sample</option>
                {Object.entries(DRY_RUN_SAMPLES).map(([key, sample]) => (
                  <option key={key} value={key}>{sample.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Actor user id (optional)</div>
              <input
                value={dryActor}
                onChange={(e) => setDryActor(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Text</div>
              <textarea
                rows={3}
                value={dryText}
                onChange={(e) => setDryText(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Links (comma-separated)</div>
              <input
                value={dryLinks}
                onChange={(e) => setDryLinks(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1">Context JSON</div>
              <textarea
                rows={3}
                value={dryContext}
                onChange={(e) => setDryContext(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-mono"
              />
            </label>
            <button
              onClick={handleDryRun}
              disabled={dryLoading}
              className="w-full rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {dryLoading ? 'Running...' : 'Run dry test'}
            </button>
            {dryError && <div className="text-xs text-red-200">{dryError}</div>}
            {dryResponse && (
              <div className="mt-2 rounded-lg border border-white/10 bg-black/40 p-2 text-xs text-white/80">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Decision</span>
                  {dryResponse.policy_applied ? (
                    <span className="rounded-full border border-white/20 px-2 py-0.5 text-[11px] text-white/70">
                      Applied: {dryResponse.policy_applied.policy_key} v{dryResponse.policy_applied.active_version}
                    </span>
                  ) : (
                    <span className="text-white/40">Applied: none</span>
                  )}
                </div>
                <div className="mt-1">Action: {mapActionKeyToLabel(dryResponse.decision?.action || '')}</div>
                <div>Categories: {(dryResponse.decision?.categories || []).join(', ') || 'none'}</div>
                <div>Confidence: {dryResponse.decision?.confidence}</div>
                <div>Severity: {dryResponse.decision?.severity}</div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-white/60">Signals</summary>
                  <pre className="mt-2 whitespace-pre-wrap text-[11px] text-white/70">
                    {JSON.stringify(dryResponse.decision?.signals || {}, null, 2)}
                  </pre>
                </details>
                {dryResponse.policy_config && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-white/60">Policy config</summary>
                    <pre className="mt-2 whitespace-pre-wrap text-[11px] text-white/70">
                      {JSON.stringify(dryResponse.policy_config || {}, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </Panel>
      </div>
      <div className="lg:col-span-8 space-y-4">
        <Panel title="Versions">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/70">
              {selectedPolicy ? `${selectedPolicy.name || selectedPolicy.key} policy versions` : 'Select a policy'}
            </div>
            <button
              onClick={openCreateModal}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5"
              disabled={!selectedPolicy}
            >
              New version
            </button>
          </div>
          {!selectedPolicy && (
            <div className="mt-3 text-sm text-white/70">Choose a policy to view versions.</div>
          )}
          {selectedPolicy && (
            <div className="mt-3 space-y-2">
              {selectedPolicy.versions.map((version) => (
                <div
                  key={version.id}
                  className={`rounded-lg border p-3 text-sm ${
                    version.id === selectedVersionId ? 'border-white/20 bg-white/10' : 'border-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">Version {version.version}</div>
                    <div className="flex items-center gap-2">
                      {version.is_active && (
                        <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-200">
                          Active
                        </span>
                      )}
                      {!version.is_active && version.id === selectedVersionId && (
                        <button
                          onClick={() => handleActivate(version.id)}
                          className="rounded-full border border-white/15 px-2 py-0.5 text-xs text-white/80 hover:bg-white/5"
                        >
                          Activate
                </button>
              )}
                    </div>
                  </div>
                  <div className="text-xs text-white/60">{new Date(version.created_at).toLocaleString()}</div>
                  <button
                    onClick={() => setSelectedVersionId(version.id)}
                    className="mt-2 text-xs text-white/60 hover:text-white"
                  >
                    View details
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Version details">
          {detailLoading && <div className="text-sm text-white/70">Loading version...</div>}
          {detailError && <div className="text-sm text-red-200">{detailError}</div>}
          {!detailLoading && !versionDetail && (
            <div className="text-sm text-white/70">Select a version to view details.</div>
          )}
          {versionDetail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-white/60">Version</div>
                  <div className="font-semibold">v{versionDetail.version}</div>
                </div>
                {versionDetail.is_active && (
                  <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-xs text-emerald-200">
                    Active
                  </span>
                )}
              </div>
              <div>
                <div className="text-xs text-white/60">Notes</div>
                <div className="text-white/80">{versionDetail.text || 'No notes.'}</div>
              </div>
              <div>
                <div className="text-xs text-white/60">Config</div>
                <pre className="mt-1 whitespace-pre-wrap rounded-lg border border-white/10 bg-black/40 p-3 text-xs text-white/70">
                  {JSON.stringify(versionDetail.config || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </Panel>
        <Panel title="Policy audit">
          {auditLoading && <div className="text-sm text-white/70">Loading audit...</div>}
          {auditError && <div className="text-sm text-red-200">{auditError}</div>}
          {!auditLoading && auditItems.length === 0 && (
            <div className="text-sm text-white/70">No policy changes yet.</div>
          )}
          {auditItems.length > 0 && (
            <div className="space-y-2 text-xs text-white/70">
              {auditItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-white/10 bg-black/30 p-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{item.action_type}</span>
                    <span>{new Date(item.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-white/60">
                    {item.policy_key ? `${item.policy_key}` : 'policy'}
                    {item.version ? ` v${item.version}` : ''}
                    {item.actor_username ? ` • ${item.actor_username}` : item.actor_id ? ` • ${item.actor_id}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-gray-900 p-6">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">New policy version</div>
              <button onClick={() => setShowCreate(false)} className="text-white/60 hover:text-white">Close</button>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              <label className="block">
                <div className="text-xs text-white/60 mb-1">Notes</div>
                <input
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <div className="text-xs text-white/60 mb-1">Config JSON</div>
                <textarea
                  rows={8}
                  value={newConfig}
                  onChange={(e) => {
                    setNewConfig(e.target.value)
                    try {
                      if (e.target.value.trim()) JSON.parse(e.target.value)
                      setNewJsonError(null)
                    } catch {
                      setNewJsonError('Config must be valid JSON.')
                    }
                  }}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-mono"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={formatJson}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"
                >
                  Format JSON
                </button>
                <button
                  onClick={resetToTemplate}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"
                  disabled={!selectedPolicyKey}
                >
                  Reset to template
                </button>
              </div>
              {newJsonError && <div className="text-xs text-red-200">{newJsonError}</div>}
              {newBackendErrors.length > 0 && (
                <div className="text-xs text-red-200">
                  {newBackendErrors.map((err) => (
                    <div key={err}>{err}</div>
                  ))}
                </div>
              )}
              {newError && <div className="text-xs text-red-200">{newError}</div>}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowCreate(false)}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={newSaving}
                  className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {newSaving ? 'Saving...' : 'Create version'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
